// ShowScreen.jsx - Livreur Zones
import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Badge, Table, Button, Alert } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import ClickBack from "../../components/buttons/ClickBack";
import { fetchLivreurZones } from "./dataService";

function ShowScreen() {
  const { id } = useParams();
  const history = useHistory();
  const [livreur, setLivreur] = useState(null);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetchLivreurZones(id);
      setLivreur(response.livreur);
      setZones(response.zones || []);
    } catch (err) {
      console.error("Error fetching zones:", err);
      setError("Erreur lors du chargement des zones");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container fluid className="p-4 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="p-4">
        <Alert variant="danger">{error}</Alert>
        <Button onClick={() => history.goBack()}>Retour</Button>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      <Row className="mb-4">
        <Col>
          <ClickBack />
          <h2 className="mt-3">
            Zones de {livreur?.nom} {livreur?.prenom}
          </h2>
        </Col>
        <Col className="text-end">
          <Button variant="primary" onClick={() => history.push(`/livreurs/zones/${id}/edit`)}>
            Modifier les zones
          </Button>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">Zones assignées ({zones.length})</h5>
            </Card.Header>
            <Card.Body>
              {zones.length === 0 ? (
                <Alert variant="warning">
                  Aucune zone assignée à ce livreur.
                  <Button
                    variant="primary"
                    size="sm"
                    className="ms-3"
                    onClick={() => history.push(`/livreurs/zones/${id}/edit`)}
                  >
                    Assigner des zones
                  </Button>
                </Alert>
              ) : (
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Zone</th>
                      <th>Type</th>
                      <th>Priorité</th>
                      <th>Statut</th>
                      <th>Notes</th>
                      <th>Assigné par</th>
                      <th>Date d'assignation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zones.map((assignment) => (
                      <tr key={assignment.id}>
                        <td>
                          <strong>{assignment.zone?.name || "Zone inconnue"}</strong>
                        </td>
                        <td>
                          <Badge bg={assignment.is_primary ? "success" : "info"}>
                            {assignment.is_primary ? "Principale" : "Secondaire"}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg="secondary">
                            Priorité {assignment.priority}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg={assignment.is_active ? "success" : "danger"}>
                            {assignment.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </td>
                        <td>
                          {assignment.notes ? (
                            <small className="text-muted">{assignment.notes}</small>
                          ) : (
                            <small className="text-muted fst-italic">Aucune note</small>
                          )}
                        </td>
                        <td>
                          <small>
                            {assignment.assigned_by_user
                              ? `${assignment.assigned_by_user.nom} ${assignment.assigned_by_user.prenom}`
                              : "Admin"}
                          </small>
                        </td>
                        <td>
                          <small>
                            {new Date(assignment.created_at).toLocaleDateString("fr-FR")}
                          </small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Zone map preview */}
      {zones.length > 0 && (
        <Row className="mt-4">
          <Col>
            <Card>
              <Card.Header className="bg-info text-white">
                <h5 className="mb-0">Résumé</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={3}>
                    <div className="text-center p-3 bg-light rounded">
                      <h4 className="text-primary">{zones.length}</h4>
                      <small className="text-muted">Zones totales</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center p-3 bg-light rounded">
                      <h4 className="text-success">
                        {zones.filter((z) => z.is_primary).length}
                      </h4>
                      <small className="text-muted">Zone principale</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center p-3 bg-light rounded">
                      <h4 className="text-info">
                        {zones.filter((z) => !z.is_primary).length}
                      </h4>
                      <small className="text-muted">Zones secondaires</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center p-3 bg-light rounded">
                      <h4 className="text-success">
                        {zones.filter((z) => z.is_active).length}
                      </h4>
                      <small className="text-muted">Zones actives</small>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
}

export default ShowScreen;
